import { mobileWatchedThreshold } from "@lib/constants";
import { type MobileVideo } from "./mobileVideoStore";

// Order videos so the funder's most-likely-next watches download first:
// their queue ("Up next") → in progress → not started → already watched.
// Pure and side-effect-free so it can gate CI without the download engine's
// native-plugin dependencies. Reads only device-local signals already on each
// MobileVideo (watch progress + recency) plus the playlist order; it
// deliberately does NOT consider the funder's track, which isn't part of the
// device-global download store the engine owns.
//
// The engine calls this at PICK time over the currently-`queued` items (not at
// enqueue time): the store's `order` is populated in catalog order before the
// funder accepts, so ordering has to happen when a download slot is filled.
export function orderByWatchPriority(
  videos: MobileVideo[],
  playlist: readonly string[],
): MobileVideo[] {
  const playlistRank = new Map<string, number>();
  playlist.forEach((id, index) => playlistRank.set(id, index));

  // Lower bucket = higher priority.
  const bucketOf = (video: MobileVideo): number => {
    if (playlistRank.has(video.id)) return 0;
    if (video.watchProgress >= mobileWatchedThreshold) return 3; // watched
    if (video.watchProgress > 0) return 1; // in progress
    return 2; // not started
  };

  // Stable sort (V8/JSC): buckets 2 and 3 preserve incoming (catalog) order.
  return [...videos].sort((a, b) => {
    const bucketA = bucketOf(a);
    const bucketB = bucketOf(b);

    if (bucketA !== bucketB) {
      return bucketA - bucketB;
    }

    // Up next: keep the funder's queue order.
    if (bucketA === 0) {
      return (playlistRank.get(a.id) ?? 0) - (playlistRank.get(b.id) ?? 0);
    }

    // In progress: most recently watched first.
    if (bucketA === 1) {
      return (b.progressClientUpdatedAt ?? "").localeCompare(
        a.progressClientUpdatedAt ?? "",
      );
    }

    return 0;
  });
}
